// A part's projects (Manufacturing projects). Mitch's rule: "it's just projects its used in, not a home project. Some
// stuff is used in different projects." A local part has no project of its own: it is in each project whose builds use
// it, worked out here by one walk over the BOM index and the products' project links, and never stored. projectScope()
// (shared/projects.ts) is read off the walk and partProjects() is its inverse, so every screen, the run check and the
// MCP agree with a project's workspace. It reads only shared/boms.ts, so the server can build it inside a writer's
// transaction (loadBomIndex(tx)).
//
// Roots of a project P, each at its current revision (currentRevision()):
// (a) products linked to P;
// (b) product BOMs whose product is linked to no project and whose revision names P (bom.project_ids, one at most: the
//     product is the finished item);
// (c) sub-assemblies nothing uses (no family's current revision has them) whose revision names P: a kit, the dongle.
//     The sub-assembly itself is in P (top). Its BOM can name several projects, and it's a root of each.
// From each root the walk follows the lines as resolve() does: supply inherited (a phantom on a "CM buys" line is the
// CM's to buy, part by part), sub-assemblies through their current revision, P's own variants skipped and sell items not
// gone into. A used sub-assembly's own bom.project_ids never count: its projects are those of what uses it.
// Draft and scheduled revisions besides the one in use are walked too: parts only they reach are on a draft (onDraft),
// for lists and filters, never readiness or money.
import {
  type BomIndex,
  type BomRow,
  bomOf,
  builtInside,
  componentsOf,
  currentRevision,
  familyKey,
  linePartIds,
  ownerLabel,
  revisionState,
  type Supply,
} from "./boms.ts";

export type PartUse = { bomId: string; lineId: string; supply: Supply; family: string };
/**
 * How a build is in a project: its product is linked to it ("product"), its BOM names it while the product is linked to
 * none ("bom"), a sub-assembly nothing uses whose BOM names it ("top"), or a line of another of its builds uses it ("line").
 */
export type ReachVia = "product" | "bom" | "top" | "line";
export type ReachFamily = { key: string; bom: BomRow; kind: "product" | "subassembly"; via: ReachVia };
export type ReachPart = {
  /** Lines of the project's builds (current revisions) that use it: as their part, in an option map or an override. */
  uses: PartUse[];
  supplies: Set<Supply>;
  /** A sub-assembly nothing uses, in the project through its own BOM's project (root c). */
  top: boolean;
};
export type ProjectReach = {
  /** Every build at its current revision, roots first. */
  families: Map<string, ReachFamily>;
  parts: Map<string, ReachPart>;
  /** Parts only a draft or scheduled revision reaches (not in parts), with those lines. */
  onDraft: Map<string, PartUse[]>;
  /** Sub-assemblies only a draft or scheduled revision reaches (not in families), by family key. */
  draftFamilies: Set<string>;
};
/** A local part's projects, each list sorted by project name; `onBom`: a current, draft or scheduled revision uses it. */
export type PartProjects = { inUse: string[]; onDraft: string[]; onBom: boolean };
export type Reach = {
  byProject: Map<string, ProjectReach>;
  /** Every local part a BOM uses or a project has; a part not here is on no BOM and in no project. */
  byPart: Map<string, PartProjects>;
  /** The projects each family is built in, by family key. */
  byFamily: Map<string, string[]>;
  /** The projects only a draft or scheduled revision brings each sub-assembly family into, by family key. */
  familiesOnDraft: Map<string, string[]>;
};

/**
 * Whether a part is in a project: a build of the project uses it, in use or only on a draft. "none": in no project.
 * inProject() (shared/projects.ts) is this for any item; the run check and the MCP use it as it is.
 */
export function partInProject(reach: Reach, partId: string, projectId: string): boolean {
  const p = reach.byPart.get(partId);
  if (projectId === "none") return !p?.inUse.length && !p?.onDraft.length;
  return !!p && (p.inUse.includes(projectId) || p.onDraft.includes(projectId));
}

/**
 * The projects a family is built in, and those only a draft or scheduled revision brings it into, each by project name:
 * the one answer for a BOM's projects (the BOMs page, the BOM editor and its picker, the MCP's get_bom).
 */
export function familyProjects(reach: Reach, key: string): { inUse: string[]; onDraft: string[] } {
  return { inUse: reach.byFamily.get(key) ?? [], onDraft: reach.familiesOnDraft.get(key) ?? [] };
}

/** A family's draft and scheduled revisions besides the one in use. */
export function pendingRevisions(ix: BomIndex, key: string): BomRow[] {
  const current = currentRevision(ix, key);
  return (ix.families.get(key) ?? []).filter((b) => b.id !== current?.id && (b.status === "draft" || revisionState(ix, b) === "scheduled"));
}

const VIA_ORDER: Record<ReachVia, number> = { product: 0, bom: 1, top: 2, line: 3 };

/**
 * Every project's builds and parts, and each part's projects. `projectOf` is a product's project link (project-setup
 * passes the links it plans); `nameOf` sorts a part's projects (by id without it).
 */
export function projectReach(ix: BomIndex, projectOf: (productId: string) => string | null | undefined, nameOf?: (projectId: string) => string): Reach {
  // What every family's current revision uses, and what any revision in use or to come does.
  const used = new Set<string>();
  const onBom = new Set<string>();
  for (const key of ix.families.keys()) {
    const bom = currentRevision(ix, key);
    for (const id of bom ? componentsOf(ix, bom.id) : []) {
      used.add(id);
      onBom.add(id);
    }
    for (const b of pendingRevisions(ix, key)) for (const id of componentsOf(ix, b.id)) onBom.add(id);
  }

  const roots = new Map<string, ReachFamily[]>();
  for (const key of ix.families.keys()) {
    const bom = currentRevision(ix, key);
    if (!bom) continue;
    let root: [string[], ReachFamily["kind"], ReachVia] | null = null;
    if (bom.shopify_product_id) {
      const linked = projectOf(bom.shopify_product_id);
      if (linked) root = [[linked], "product", "product"];
      else if (bom.project_ids.length) root = [bom.project_ids.slice(0, 1), "product", "bom"];
    } else if (bom.item_id && bom.project_ids.length && !used.has(bom.item_id)) {
      // A kit or a board nothing uses is in every project its BOM names
      const owner = ix.itemById.get(bom.item_id);
      if (owner && owner.source === "local" && !owner.archived) root = [bom.project_ids, "subassembly", "top"];
    }
    if (!root) continue;
    const [projectIds, kind, via] = root;
    for (const projectId of projectIds) {
      const list = roots.get(projectId) ?? roots.set(projectId, []).get(projectId);
      list?.push({ key, bom, kind, via });
    }
  }
  const variantsOf = new Map<string, Set<string>>(); // project → its own variants (never one of its parts)
  for (const [productId, variants] of ix.variantsByProduct) {
    const projectId = projectOf(productId);
    if (!projectId) continue;
    const set = variantsOf.get(projectId) ?? variantsOf.set(projectId, new Set()).get(projectId);
    for (const v of variants) set?.add(v.id);
  }

  const byProject = new Map<string, ProjectReach>();
  for (const [projectId, list] of roots) {
    const skip = variantsOf.get(projectId) ?? new Set<string>();
    const families = new Map<string, ReachFamily>();
    const parts = new Map<string, ReachPart>();
    const onDraft = new Map<string, PartUse[]>();
    const draftFamilies = new Set<string>();
    // Walked once per family and supply inherited from above: a phantom put together on a "CM buys" line is the CM's
    // to buy, part by part, as resolve() has it (a stocked sub-assembly's parts keep their own lines' supply).
    const queue: { bom: BomRow; inherit: Supply | null }[] = [];
    const walked = new Set<string>();
    const addFamily = (bom: BomRow, kind: ReachFamily["kind"], via: ReachVia, inherit: Supply | null = null) => {
      const key = familyKey(bom);
      if (!families.has(key)) families.set(key, { key, bom, kind, via });
      if (walked.has(`${key}|${inherit ?? ""}`)) return;
      walked.add(`${key}|${inherit ?? ""}`);
      queue.push({ bom, inherit });
    };
    const partFor = (id: string) => {
      let p = parts.get(id);
      if (!p && ix.itemById.has(id)) parts.set(id, (p = { uses: [], supplies: new Set(), top: false }));
      return p;
    };
    const walk = ({ bom, inherit }: { bom: BomRow; inherit: Supply | null }) => {
      const key = familyKey(bom);
      for (const line of ix.linesByBom.get(bom.id) ?? []) {
        const supply = inherit ?? line.supply;
        for (const id of linePartIds(ix, line)) {
          if (skip.has(id)) continue;
          const p = partFor(id);
          if (!p) continue;
          if (!p.uses.some((u) => u.lineId === line.id && u.supply === supply)) p.uses.push({ bomId: bom.id, lineId: line.id, supply, family: key });
          p.supplies.add(supply);
          const item = ix.itemById.get(id);
          const sub = item && item.source === "local" ? bomOf(ix, item) : undefined;
          if (sub) addFamily(sub, "subassembly", "line", supply === "cm_buys" && builtInside(sub, bom.make_vendor_id, supply) ? "cm_buys" : null);
        }
      }
    };
    // Linked products' builds first, then product BOMs that name the project, then its top sub-assemblies with what
    // they reach (each by name, so the order doesn't follow the database's).
    const sorted = [...list].sort((a, b) => VIA_ORDER[a.via] - VIA_ORDER[b.via] || ownerLabel(ix, a.bom).localeCompare(ownerLabel(ix, b.bom)));
    let at = 0;
    const drain = () => {
      for (; at < queue.length; at++) walk(queue[at]);
    };
    for (const r of sorted) if (r.via !== "top") addFamily(r.bom, r.kind, r.via);
    drain();
    for (const r of sorted) {
      if (r.via !== "top" || !r.bom.item_id) continue;
      addFamily(r.bom, r.kind, r.via);
      const p = partFor(r.bom.item_id);
      if (p) p.top = true;
    }
    drain();

    // On a draft: every build's other draft and scheduled revisions, with the supply its current one was walked with;
    // a sub-assembly first met there is walked at its current revision.
    const pending: { bom: BomRow; inherit: Supply | null }[] = [];
    const seen = new Set<string>();
    const addPending = (bom: BomRow, inherit: Supply | null) => {
      const id = `${bom.id}|${inherit ?? ""}`;
      if (seen.has(id)) return;
      seen.add(id);
      pending.push({ bom, inherit });
    };
    for (const { bom, inherit } of queue) for (const b of pendingRevisions(ix, familyKey(bom))) addPending(b, inherit);
    for (let at = 0; at < pending.length; at++) {
      const { bom, inherit } = pending[at];
      const key = familyKey(bom);
      for (const line of ix.linesByBom.get(bom.id) ?? []) {
        const supply = inherit ?? line.supply;
        for (const id of linePartIds(ix, line)) {
          if (skip.has(id) || !ix.itemById.has(id)) continue;
          if (!parts.has(id)) {
            let uses = onDraft.get(id);
            if (!uses) onDraft.set(id, (uses = []));
            if (!uses.some((u) => u.lineId === line.id && u.supply === supply)) uses.push({ bomId: bom.id, lineId: line.id, supply, family: key });
          }
          const item = ix.itemById.get(id);
          const sub = item && item.source === "local" ? bomOf(ix, item) : undefined;
          if (sub && !families.has(familyKey(sub))) {
            draftFamilies.add(familyKey(sub));
            addPending(sub, supply === "cm_buys" && builtInside(sub, bom.make_vendor_id, supply) ? "cm_buys" : null);
          }
        }
      }
    }
    byProject.set(projectId, { families, parts, onDraft, draftFamilies });
  }

  const byPart = new Map<string, PartProjects>();
  const entry = (id: string) => {
    let e = byPart.get(id);
    if (!e && ix.itemById.get(id)?.source === "local") byPart.set(id, (e = { inUse: [], onDraft: [], onBom: onBom.has(id) }));
    return e;
  };
  const byFamily = new Map<string, string[]>();
  const familiesOnDraft = new Map<string, string[]>();
  for (const [projectId, r] of byProject) {
    for (const id of r.parts.keys()) entry(id)?.inUse.push(projectId);
    for (const id of r.onDraft.keys()) entry(id)?.onDraft.push(projectId);
    for (const key of r.families.keys()) (byFamily.get(key) ?? byFamily.set(key, []).get(key))?.push(projectId);
    for (const key of r.draftFamilies) (familiesOnDraft.get(key) ?? familiesOnDraft.set(key, []).get(key))?.push(projectId);
  }
  for (const id of onBom) entry(id);
  const name = nameOf ?? ((id: string) => id);
  const byName = (a: string, b: string) => name(a).localeCompare(name(b)) || a.localeCompare(b);
  for (const e of byPart.values()) {
    e.inUse.sort(byName);
    e.onDraft.sort(byName);
  }
  for (const list of [...byFamily.values(), ...familiesOnDraft.values()]) list.sort(byName);
  return { byProject, byPart, byFamily, familiesOnDraft };
}
